import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

// Where this visit started: kept for the whole session so an enquiry sent three pages later
// still says which page, search or ad brought the visitor in.
export interface FirstTouch {
  landing_page: string;
  referrer: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  gclid: string;
}

const STORAGE_KEY = 'atm_first_touch';

@Injectable({ providedIn: 'root' })
export class AttributionService {
  private memory: FirstTouch | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /** Call once at app start; later calls in the same session keep the first touch. */
  recordFirstTouch(): void {
    if (!isPlatformBrowser(this.platformId) || this.read()) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const touch: FirstTouch = {
      landing_page: window.location.pathname + window.location.search,
      referrer: this.externalReferrer(),
      utm_source: params.get('utm_source') ?? '',
      utm_medium: params.get('utm_medium') ?? '',
      utm_campaign: params.get('utm_campaign') ?? '',
      gclid: params.get('gclid') ?? '',
    };
    this.memory = touch;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(touch));
    } catch {
      // private mode or blocked storage: the in-memory copy still covers this page
    }
  }

  /**
   * One line for staff, appended to the enquiry notes (the backend has no columns for these yet).
   * Deterministic per page and session, so the backend's duplicate check still matches a resubmission.
   */
  sentFromLine(source: string): string {
    if (!isPlatformBrowser(this.platformId)) {
      return '';
    }
    const touch = this.read();
    let line = `— Sent from ${window.location.pathname} (${source})`;
    if (touch) {
      if (touch.landing_page && touch.landing_page !== window.location.pathname) {
        line += ` · landed on ${touch.landing_page}`;
      }
      if (touch.referrer) {
        line += ` · via ${touch.referrer}`;
      }
      if (touch.utm_source || touch.utm_medium || touch.utm_campaign) {
        line += ` · ${touch.utm_source || '-'}/${touch.utm_medium || '-'}/${touch.utm_campaign || '-'}`;
      }
      if (touch.gclid) {
        line += ' · gclid';
      }
    }
    return line;
  }

  private read(): FirstTouch | null {
    if (this.memory) {
      return this.memory;
    }
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      this.memory = stored ? JSON.parse(stored) : null;
    } catch {
      this.memory = null;
    }
    return this.memory;
  }

  private externalReferrer(): string {
    try {
      if (!document.referrer) {
        return '';
      }
      const host = new URL(document.referrer).hostname.replace(/^www\./, '');
      return host === window.location.hostname.replace(/^www\./, '') ? '' : host;
    } catch {
      return '';
    }
  }
}
